<template>
  <div class="space-y-8">
    <div>
      <h1 class="text-base/7 font-semibold text-gray-900 dark:text-white">
        {{ isEdit ? 'Edit cache' : 'New cache' }}
      </h1>
      <p class="mt-1 max-w-xl text-sm text-gray-500 dark:text-gray-400">
        Any S3-compatible object storage works, for example Hetzner Object Storage, AWS S3,
        Cloudflare R2, DigitalOcean Spaces or MinIO.
      </p>
    </div>

    <p v-if="loading" class="text-sm text-gray-500 dark:text-gray-400">Loading cache…</p>
    <form v-else class="max-w-xl space-y-6" novalidate @submit.prevent="onSubmit">
      <div>
        <label for="cache-name" :class="labelClass">Name</label>
        <input
          id="cache-name"
          v-model="form.name"
          type="text"
          :class="inputClass('name')"
          data-1p-ignore
        />
        <p v-if="errors.name" :class="errorClass">{{ errors.name }}</p>
      </div>

      <div>
        <label for="cache-server" :class="labelClass">Server address</label>
        <input
          id="cache-server"
          v-model.trim="form.serverAddress"
          type="text"
          :class="inputClass('serverAddress')"
          placeholder="fsn1.your-objectstorage.com"
          aria-describedby="cache-server-desc"
        />
        <p v-if="errors.serverAddress" :class="errorClass">{{ errors.serverAddress }}</p>
        <p v-else id="cache-server-desc" :class="hintClass">
          Host and optional port, without <code>https://</code> and without the bucket.
        </p>
      </div>

      <div class="flex gap-3">
        <input
          id="cache-insecure"
          v-model="form.insecure"
          type="checkbox"
          :class="checkboxClass"
          aria-describedby="cache-insecure-desc"
        />
        <div class="text-sm/6">
          <label for="cache-insecure" class="font-medium text-gray-900 dark:text-white"
            >Use plain HTTP</label
          >
          <p id="cache-insecure-desc" class="text-gray-500 dark:text-gray-400">
            Only for servers without TLS. Cache contents and credentials travel unencrypted.
          </p>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <label for="cache-bucket" :class="labelClass">Bucket</label>
          <input
            id="cache-bucket"
            v-model.trim="form.bucketName"
            type="text"
            :class="inputClass('bucketName')"
          />
          <p v-if="errors.bucketName" :class="errorClass">{{ errors.bucketName }}</p>
        </div>
        <div>
          <label for="cache-region" :class="labelClass">Region</label>
          <input
            id="cache-region"
            v-model.trim="form.bucketLocation"
            type="text"
            :class="inputClass('bucketLocation')"
            placeholder="fsn1, eu-central-1, auto"
          />
          <p v-if="errors.bucketLocation" :class="errorClass">{{ errors.bucketLocation }}</p>
        </div>
      </div>

      <div>
        <label for="cache-prefix" :class="labelClass"
          >Path prefix <span class="font-normal text-gray-500">(optional)</span></label
        >
        <input
          id="cache-prefix"
          v-model.trim="form.pathPrefix"
          type="text"
          :class="inputClass('pathPrefix')"
          placeholder="gitlab-runner"
        />
        <p v-if="errors.pathPrefix" :class="errorClass">{{ errors.pathPrefix }}</p>
        <p v-else :class="hintClass">Lets several caches share one bucket.</p>
      </div>

      <div class="flex gap-3">
        <input
          id="cache-shared"
          v-model="form.shared"
          type="checkbox"
          :class="checkboxClass"
          aria-describedby="cache-shared-desc"
        />
        <div class="text-sm/6">
          <label for="cache-shared" class="font-medium text-gray-900 dark:text-white"
            >Share between runners</label
          >
          <p id="cache-shared-desc" class="text-gray-500 dark:text-gray-400">
            Runners using this cache read and write the same entries. Without sharing, each runner
            keeps its own entries in the bucket.
          </p>
        </div>
      </div>

      <div>
        <label for="cache-path-style" :class="labelClass">Addressing style</label>
        <select id="cache-path-style" v-model="pathStyle" :class="selectClass">
          <option value="auto">Detect automatically</option>
          <option value="path">Path style (server/bucket)</option>
          <option value="virtual">Virtual-hosted style (bucket.server)</option>
        </select>
      </div>

      <div>
        <label for="cache-access-key" :class="labelClass">Access key ID</label>
        <input
          id="cache-access-key"
          v-model.trim="form.accessKeyId"
          type="text"
          :class="inputClass('accessKeyId')"
          autocomplete="off"
          data-1p-ignore
        />
        <p v-if="errors.accessKeyId" :class="errorClass">{{ errors.accessKeyId }}</p>
      </div>

      <div>
        <label for="cache-secret" :class="labelClass">Secret access key</label>
        <input
          id="cache-secret"
          v-model.trim="form.secretKey"
          type="password"
          :class="inputClass('secretKey')"
          :placeholder="isEdit ? 'Unchanged' : ''"
          autocomplete="new-password"
          data-1p-ignore
        />
        <p v-if="errors.secretKey" :class="errorClass">{{ errors.secretKey }}</p>
        <p v-else :class="hintClass">
          {{
            isEdit
              ? 'Stored encrypted and never shown again. Leave empty to keep the current key.'
              : 'Stored encrypted and never shown again.'
          }}
        </p>
      </div>

      <div
        v-if="usedBy.length > 0"
        class="rounded-md bg-yellow-50 p-4 dark:bg-yellow-500/10"
        data-testid="cache-used-by-warning"
      >
        <p class="text-sm text-yellow-800 dark:text-yellow-200">
          Used by {{ usedBy.map((r) => r.name).join(', ') }}. Changing anything except the name
          recreates the VMs of the active ones.
        </p>
      </div>

      <div class="space-y-2">
        <button
          type="button"
          class="inline-flex items-center justify-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white/10 dark:text-white dark:inset-ring-white/5 dark:hover:bg-white/20"
          :disabled="form.insecure || testing"
          data-testid="test-connection"
          @click="onTest"
        >
          {{ testing ? 'Testing…' : 'Test connection' }}
        </button>
        <p v-if="form.insecure" :class="hintClass" data-testid="test-insecure-hint">
          Test connection is only available for HTTPS endpoints.
        </p>
        <p
          v-else-if="testResult"
          :class="[
            'text-sm',
            testResult.success
              ? 'text-green-700 dark:text-green-400'
              : 'text-red-600 dark:text-red-400',
          ]"
          role="status"
          data-testid="test-result"
        >
          {{ testResult.message }}
        </p>
        <p v-else-if="testError" class="text-sm text-red-600 dark:text-red-400" role="alert">
          {{ testError }}
        </p>
      </div>

      <div class="flex flex-wrap items-center gap-3">
        <button
          type="button"
          class="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 dark:border-white/10 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
          @click="router.push({ name: 'Caches' })"
        >
          Cancel
        </button>
        <button
          type="submit"
          class="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="saving"
          data-testid="save-cache"
        >
          {{ saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create cache' }}
        </button>
      </div>
      <PlanRequiredNotice v-if="planRequired">
        Changing the connection recreates the running runners that use this cache.
      </PlanRequiredNotice>
      <p v-else-if="serverError" class="text-sm text-red-600 dark:text-red-400" role="alert">
        {{ serverError }}
      </p>
    </form>

    <ModalPanel :open="recreateRunners !== null" @close="recreateRunners = null">
      <DialogTitle as="h3" class="text-base font-semibold text-gray-900 dark:text-white">
        Recreate runners?
      </DialogTitle>
      <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
        Saving this change deletes and recreates the VMs of the active runners so they use the new
        cache settings. Jobs running on them are interrupted. Unknown runners are not recreated and
        keep the previous settings until you edit or recreate them.
      </p>
      <ul
        class="mt-3 list-disc pl-5 text-sm text-gray-700 dark:text-gray-300"
        data-testid="recreate-runners"
      >
        <li v-for="runner in recreateRunners ?? []" :key="runner.runnerId">
          {{ runner.name
          }}<span v-if="runner.recreate === false" class="text-gray-500 dark:text-gray-400">
            (unknown, not recreated automatically)</span
          >
        </li>
      </ul>
      <div class="mt-5 gap-3 sm:mt-4 sm:flex sm:flex-row-reverse">
        <button
          type="button"
          class="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-500 disabled:opacity-50 sm:w-auto dark:bg-red-500 dark:hover:bg-red-400"
          :disabled="saving"
          data-testid="confirm-recreate"
          @click="save(true)"
        >
          {{ saving ? 'Saving…' : 'Save and recreate' }}
        </button>
        <button
          type="button"
          class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto dark:bg-white/10 dark:text-white dark:inset-ring-white/5 dark:hover:bg-white/20"
          :disabled="saving"
          @click="recreateRunners = null"
        >
          Cancel
        </button>
      </div>
    </ModalPanel>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { DialogTitle } from '@headlessui/vue';
import ModalPanel from '@/components/ModalPanel.vue';
import {
  asProblem,
  conflictRunners,
  createCache,
  getCache,
  testCacheConnection,
  testSavedCacheConnection,
  updateCache,
  type CacheProblem,
  type CacheTestResult,
  type RunnerCacheRequest,
  type RunnerCacheUsage,
} from '@/api/caches';
import { useToastStore } from '@/stores/toast';
import PlanRequiredNotice from '@/components/PlanRequiredNotice.vue';
import { isSubscriptionRequired } from '@/lib/subscription-required';

type Field =
  | 'name'
  | 'serverAddress'
  | 'bucketName'
  | 'bucketLocation'
  | 'pathPrefix'
  | 'accessKeyId'
  | 'secretKey';

const route = useRoute();
const router = useRouter();
const toast = useToastStore();
const cacheId = computed(() => (route.params.id ? String(route.params.id) : ''));
const isEdit = computed(() => cacheId.value !== '');

const form = reactive({
  name: '',
  serverAddress: '',
  insecure: false,
  bucketName: '',
  bucketLocation: '',
  pathPrefix: '',
  shared: true,
  accessKeyId: '',
  secretKey: '',
});
const pathStyle = ref<'auto' | 'path' | 'virtual'>('auto');
const errors = reactive<Partial<Record<Field, string>>>({});
const usedBy = ref<RunnerCacheUsage[]>([]);
const loading = ref(false);
const saving = ref(false);
const serverError = ref('');
const planRequired = ref(false);
const testing = ref(false);
const testResult = ref<CacheTestResult | null>(null);
const testError = ref('');
const recreateRunners = ref<RunnerCacheUsage[] | null>(null);

const labelClass = 'block text-sm/6 font-medium text-gray-900 dark:text-white';
const hintClass = 'mt-2 text-sm text-gray-500 dark:text-gray-400';
const errorClass = 'mt-2 text-sm text-red-600 dark:text-red-400';
const checkboxClass =
  'mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 dark:border-white/10 dark:bg-white/5';
const selectClass =
  'mt-2 w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:*:bg-gray-800';

function inputClass(field: Field) {
  const base =
    'mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6 dark:bg-white/5';
  return errors[field]
    ? `${base} text-red-900 outline-red-300 focus:outline-red-600 dark:text-red-400 dark:outline-red-500/50`
    : `${base} text-gray-900 outline-gray-300 focus:outline-indigo-600 dark:text-white dark:outline-white/10 dark:placeholder:text-gray-500`;
}

const hostPort =
  /^(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*|\[[0-9A-Fa-f:.]+\])(?::\d{1,5})?$/;

function validate(): boolean {
  (Object.keys(errors) as Field[]).forEach((key) => delete errors[key]);
  if (!form.name.trim()) errors.name = 'Name is required.';
  if (!form.serverAddress) errors.serverAddress = 'Server address is required.';
  else if (/^[a-z]+:\/\//i.test(form.serverAddress))
    errors.serverAddress = 'Leave out the scheme, for example fsn1.your-objectstorage.com.';
  else if (!hostPort.test(form.serverAddress))
    errors.serverAddress = 'Enter a host name with an optional port.';
  if (!/^[A-Za-z0-9][A-Za-z0-9._-]{1,61}[A-Za-z0-9]$/.test(form.bucketName))
    errors.bucketName =
      'Enter a bucket name of 3 to 63 letters, digits, dots, dashes or underscores.';
  if (!/^[A-Za-z0-9_-]{1,64}$/.test(form.bucketLocation))
    errors.bucketLocation = 'Region is required.';
  if (form.pathPrefix && !/^\/?[A-Za-z0-9._-]+(\/[A-Za-z0-9._-]+)*\/?$/.test(form.pathPrefix))
    errors.pathPrefix = 'Use letters, digits, dots, dashes and underscores, separated by slashes.';
  if (!form.accessKeyId) errors.accessKeyId = 'Access key ID is required.';
  if (!isEdit.value && !form.secretKey) errors.secretKey = 'Secret access key is required.';
  return Object.keys(errors).length === 0;
}

function connection() {
  return {
    serverAddress: form.serverAddress,
    insecure: form.insecure,
    bucketName: form.bucketName,
    bucketLocation: form.bucketLocation,
    pathPrefix: form.pathPrefix || null,
    shared: form.shared,
    pathStyle: pathStyle.value === 'auto' ? null : pathStyle.value === 'path',
    accessKeyId: form.accessKeyId,
    ...(form.secretKey ? { secretKey: form.secretKey } : {}),
  };
}

function problemMessage(e: CacheProblem, fallback: string) {
  const data = e?.response?.data;
  if (data?.code === 'INVALID_CACHE' && data?.field) return `Check the ${fieldLabel(data.field)}.`;
  return data?.detail || e?.message || fallback;
}

function fieldLabel(field: string) {
  const labels: Record<string, string> = {
    name: 'name',
    serverAddress: 'server address',
    bucketName: 'bucket',
    bucketLocation: 'region',
    pathPrefix: 'path prefix',
    accessKeyId: 'access key ID',
    secretKey: 'secret access key',
  };
  return labels[field] ?? field;
}

async function onTest() {
  testResult.value = null;
  testError.value = '';
  if (!validate()) return;
  testing.value = true;
  try {
    testResult.value = isEdit.value
      ? await testSavedCacheConnection(cacheId.value, connection())
      : await testCacheConnection(connection());
  } catch (error) {
    const e = asProblem(error);
    testError.value = problemMessage(e, 'The connection test failed.');
  } finally {
    testing.value = false;
  }
}

async function onSubmit() {
  serverError.value = '';
  if (!validate()) return;
  await save(false);
}

async function save(confirmRecreate: boolean) {
  saving.value = true;
  serverError.value = '';
  planRequired.value = false;
  const payload: RunnerCacheRequest = { name: form.name.trim(), ...connection() };
  try {
    if (isEdit.value) {
      const result = await updateCache(cacheId.value, payload, confirmRecreate);
      recreateRunners.value = null;
      toast.success(
        result.recreatedRunners.length > 0
          ? `Cache saved. Recreating ${result.recreatedRunners.map((r) => r.name).join(', ')}.`
          : 'Cache saved.'
      );
    } else {
      await createCache(payload);
      toast.success('Cache created.');
    }
    await router.push({ name: 'Caches' });
  } catch (error) {
    const e = asProblem(error);
    planRequired.value = isSubscriptionRequired(error);
    const runners = conflictRunners(error, 'CACHE_RECREATE_REQUIRED');
    if (runners && !confirmRecreate) {
      recreateRunners.value = runners;
    } else {
      recreateRunners.value = null;
      const busy = conflictRunners(error, 'RUNNER_LOCKED');
      serverError.value = busy?.length
        ? `These runners are busy, try again later: ${busy.map((r) => r.name).join(', ')}.`
        : problemMessage(e, 'Failed to save the cache.');
    }
  } finally {
    saving.value = false;
  }
}

watch(
  () => [
    form.serverAddress,
    form.insecure,
    form.bucketName,
    form.bucketLocation,
    form.pathPrefix,
    form.accessKeyId,
    form.secretKey,
    pathStyle.value,
  ],
  () => {
    testResult.value = null;
    testError.value = '';
  }
);

onMounted(async () => {
  if (!isEdit.value) return;
  loading.value = true;
  try {
    const cache = await getCache(cacheId.value);
    form.name = cache.name;
    form.serverAddress = cache.serverAddress;
    form.insecure = cache.insecure;
    form.bucketName = cache.bucketName;
    form.bucketLocation = cache.bucketLocation;
    form.pathPrefix = cache.pathPrefix ?? '';
    form.shared = cache.shared;
    form.accessKeyId = cache.accessKeyId;
    pathStyle.value = cache.pathStyle === null ? 'auto' : cache.pathStyle ? 'path' : 'virtual';
    usedBy.value = cache.usedBy;
  } catch (error) {
    const e = asProblem(error);
    serverError.value = problemMessage(e, 'Failed to load the cache.');
  } finally {
    loading.value = false;
  }
});
</script>
